import type { ComponentType } from 'react';
import type { NombreIcono } from '../componentes/iconos';
import type { TarjetaAyuda } from '../marco/cabecera';
import { Auditoria } from './auditoria/Auditoria';
import { Catalogo } from './catalogo/Catalogo';
import { Errores } from './errores/Errores';
import { Negocios } from './negocios/Negocios';
import { Partners } from './partners/Partners';
import type { PropsSeccion } from './tipos';
import { YCloud } from './ycloud/YCloud';

/**
 * **Registro de las secciones del backoffice** (prototipo `NAV`, `PAGES` y `HELP` de `1.20-1.22-backoffice.html`). El
 * menú es fijo en el panel, como el del portal de partners (G1101): fuera de un negocio no hay `getMyNavigation`. Lo que
 * el rol no puede ver no sale (G120): Soporte ve Negocios, Cola de errores, Indicadores y Auditoría; el resto, solo el
 * Superadmin (`roles-permisos.md`, «Plataforma»). Si llega por la dirección, «sin permiso».
 *
 * Para programar una sección (1.21, 1.22, 7.4): su carpeta `backoffice/<seccion>/` y, aquí, su `Pantalla`.
 */
export interface SeccionBackoffice {
    clave: string;
    etiqueta: string;
    /** Etiqueta corta de la barra inferior del móvil. */
    corta: string;
    icono: NombreIcono;
    /** `todos` (Superadmin y Soporte) o solo el Superadmin. */
    quien: 'todos' | 'superadmin';
    titulo: string;
    subtitulo: string;
    ayuda: readonly TarjetaAyuda[];
    /** La pantalla; `null` es su hueco hasta que se programe. */
    Pantalla: ComponentType<PropsSeccion> | null;
}

const ayuda = (lista: readonly [NombreIcono, string, string][]): TarjetaAyuda[] => lista.map(([icono, titulo, texto]) => ({ icono, titulo, texto }));

export const SECCIONES: readonly SeccionBackoffice[] = [
    {
        clave: 'negocios',
        etiqueta: 'Negocios',
        corta: 'Negocios',
        icono: 'building-2',
        quien: 'todos',
        titulo: 'Negocios',
        subtitulo: 'Todos los negocios de Reservaria. Entra en uno para ver su plan, su equipo o abrir un acceso de soporte.',
        ayuda: ayuda([
            ['building-2', 'Lista de negocios', 'Busca por nombre, email o ciudad. La lista va por páginas: «Siguiente» trae la próxima.'],
            ['triangle-alert', 'Con problemas', 'Negocios con avisos abiertos en la cola de errores o en su campana. El triángulo rojo los marca en la lista.'],
            ['headset', 'Entrar en un negocio', 'Siempre con motivo. Caduca a los 60 minutos y el propietario lo ve en su Actividad.'],
            ['user-cog', 'Suplantar', 'Solo Superadmin, con un código de dos pasos de tu aplicación cada vez.'],
        ]),
        Pantalla: Negocios,
    },
    {
        clave: 'errores',
        etiqueta: 'Cola de errores',
        corta: 'Errores',
        icono: 'triangle-alert',
        quien: 'todos',
        titulo: 'Cola de errores',
        subtitulo: 'Avisos de facturación, de Stripe de los negocios y de mensajería que no se han podido aplicar.',
        ayuda: ayuda([
            ['triangle-alert', 'Una sola cola', 'Junta los avisos de facturación de Reservaria, de Stripe de los negocios y de los proveedores de mensajería.'],
            ['refresh-cw', 'Reintentar', 'Vuelve a procesar el aviso tal como llegó.'],
            ['check', 'Resuelto', 'Para lo que ya se arregló a mano y no se va a reprocesar. Pide motivo y queda en Auditoría.'],
        ]),
        Pantalla: Errores,
    },
    {
        clave: 'catalogo',
        etiqueta: 'Catálogo y precios',
        corta: 'Catálogo',
        icono: 'tags',
        quien: 'superadmin',
        titulo: 'Catálogo y precios',
        subtitulo: 'La base, los bloques, los paquetes y los cupones. Un precio nuevo vale para los contratos nuevos.',
        ayuda: ayuda([
            ['tags', 'Precios', 'Un precio nuevo empieza el día que elijas y vale solo para los contratos nuevos. Quien ya lo tiene sigue igual.'],
            ['percent', 'Cupones', 'Se crean aquí y la aplicación los crea en Stripe. No se editan: para cambiar uno, se crea otro.'],
        ]),
        Pantalla: Catalogo,
    },
    {
        clave: 'partners',
        etiqueta: 'Partners',
        corta: 'Partners',
        icono: 'handshake',
        quien: 'superadmin',
        titulo: 'Partners',
        subtitulo: 'Agencias y personas que traen negocios. Cobran el 20 % de lo que paga cada uno.',
        ayuda: ayuda([
            ['handshake', 'Solicitudes', 'Aprueba o rechaza a quien pide ser partner. Al aprobarlo, su código empieza a atribuirle negocios.'],
            ['wallet', 'Liquidaciones', 'Se cierran el día 5 y se pagan por transferencia antes del 15.'],
            ['building-2', 'Atribuir a mano', 'Para un negocio que se dio de alta sin el código: en Negocios, su ficha › Resumen › «Atribuir a un partner», con motivo.'],
        ]),
        Pantalla: Partners,
    },
    {
        clave: 'ycloud',
        etiqueta: 'Conciliación YCloud',
        corta: 'YCloud',
        icono: 'scale',
        quien: 'superadmin',
        titulo: 'Conciliación YCloud',
        subtitulo: 'Lo que YCloud nos cobra por WhatsApp frente a lo que hemos contado nosotros, mes a mes.',
        ayuda: ayuda([
            ['scale', 'Diferencias', 'Corregir el registro, ajustar el paquete del negocio, reclamar a YCloud o asumir el coste.'],
            ['plus', 'Coste del trimestre', 'Cuando Meta anuncia tarifas nuevas, registra el coste por categoría con su fecha.'],
            ['percent', 'Margen', 'Lo que se cobra a los negocios por sus paquetes menos el coste de Meta, por mes y por categoría, sin IVA.'],
        ]),
        Pantalla: YCloud,
    },
    {
        clave: 'indicadores',
        etiqueta: 'Indicadores',
        corta: 'Indicadores',
        icono: 'chart-line',
        quien: 'todos',
        titulo: 'Indicadores de plataforma',
        subtitulo: 'Las cifras de Reservaria y las versiones de cada indicador. Siempre agregadas por negocio.',
        ayuda: ayuda([
            ['layers', 'Versiones', 'Cambiar cómo se calcula un indicador es publicar una versión nueva, nunca recalcular encima. La declara el manifiesto del módulo.'],
            ['refresh-cw', 'Reconstruir', 'La versión nueva se construye en paralelo, solo con el archivo de eventos de Analítica. El panel sigue con la vigente.'],
            ['git-compare', 'Revisar y aprobar', 'Mira las diferencias por negocio y mes. Al aprobar, todos los negocios cambian a la vez y la anterior se borra a los 30 días.'],
        ]),
        Pantalla: null,
    },
    {
        clave: 'auditoria',
        etiqueta: 'Auditoría',
        corta: 'Auditoría',
        icono: 'history',
        quien: 'todos',
        titulo: 'Auditoría de plataforma',
        subtitulo: 'Lo que hace el equipo de Reservaria: accesos, precios, cupones, partners y conciliaciones. No se edita ni se borra.',
        ayuda: ayuda([['history', 'Auditoría', 'Cada acción del equipo de Reservaria con su motivo. No se edita ni se borra.']]),
        Pantalla: Auditoria,
    },
];

/** La primera sección, a la que se llega sin `?seccion=` y a la que lleva «Ir a Negocios». */
export const SECCION_INICIAL = 'negocios';

/** Las secciones que ve el rol, en el orden del menú (G120: lo que no puede ver no sale). */
export function seccionesDe(superadmin: boolean): SeccionBackoffice[] {
    return SECCIONES.filter((s) => superadmin || s.quien === 'todos');
}

/** La sección de `?seccion=` (sin ella o desconocida, Negocios). */
export function seccionDe(busqueda: URLSearchParams): SeccionBackoffice {
    const clave = busqueda.get('seccion') ?? SECCION_INICIAL;
    return SECCIONES.find((s) => s.clave === clave) ?? (SECCIONES[0] as SeccionBackoffice);
}
